// Pulse Crush — Arcade Game 2 (match-3). Match 3+ items to fill the level's score bar.
// You have a limited number of MOVES (no timer, no lives — Points 34/35/36). Every time
// the score bar crosses a "slab" a skill question pops up: a correct answer earns points
// and bonus moves; a wrong answer just closes and you keep matching toward the next slab.
// Fill the bar to CLEAR the level. Run out of moves and the level FAILS.
// Server is authoritative for the question gate + points; the match-3 board runs client-side.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, mediaUrl } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
    Package, Wrench, Zap, Lightbulb, ToggleRight, Cable, Move, Trophy,
    Loader2, CheckCircle2, XCircle, RotateCcw, Play, Lock, ChevronLeft, Target, Sparkles,
} from 'lucide-react';

const SIZE = 8;
// Green Arcade theme (Arcade Colour Guide) — doc Point 23.
const CR = {
    body: '#02160E', panel: '#013022', gold: '#00DB79', cream: '#9DF5CE',
    ink: '#01130C', grid: '#01120B', danger: '#EF4444', green: '#22C55E',
};
// Real tool artwork used as the default match-3 tiles (doc Point 25 — no plain boxes).
// Admins can still override per-type via the game config (tileImg).
const DEFAULT_TILE_IMG = {
    propulse_pack: '/arcade/tiles/propulse_pack.png',
    tools: '/arcade/tiles/tools.png',
    short_circuit: '/arcade/tiles/short_circuit.png',
    bulb: '/arcade/tiles/bulb.png',
    switch: '/arcade/tiles/switch.png',
    wire: '/arcade/tiles/wire.png',
};
const tileSrc = (img) => (img && img.startsWith('/') ? img : mediaUrl(img));
const TILE_META = {
    propulse_pack: { color: '#F5C542', ring: '#7A5E00', Icon: Package, label: 'ProPulse Pack' },
    tools: { color: '#3B82F6', ring: '#1E3A8A', Icon: Wrench, label: 'Tools' },
    short_circuit: { color: '#EF4444', ring: '#7F1D1D', Icon: Zap, label: 'Short-Circuit' },
    bulb: { color: '#FBBF24', ring: '#92650A', Icon: Lightbulb, label: 'Bulb' },
    switch: { color: '#22C55E', ring: '#14532D', Icon: ToggleRight, label: 'Switch' },
    wire: { color: '#F97316', ring: '#7C2D12', Icon: Cable, label: 'Wire' },
};
const TYPES = Object.keys(TILE_META);
const uid = () => Math.random().toString(36).slice(2);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const bonus = (k) => (k >= 5 ? 2 : k >= 4 ? 1.5 : 1);

function randTile() { return { id: uid(), type: TYPES[Math.floor(Math.random() * TYPES.length)] }; }

function newBoard() {
    const b = [];
    for (let r = 0; r < SIZE; r++) {
        const row = [];
        for (let c = 0; c < SIZE; c++) {
            let t = randTile();
            // avoid initial 3-in-a-row
            while (
                (c >= 2 && row[c - 1].type === t.type && row[c - 2].type === t.type) ||
                (r >= 2 && b[r - 1][c].type === t.type && b[r - 2][c].type === t.type)
            ) { t = randTile(); }
            row.push(t);
        }
        b.push(row);
    }
    return b;
}

const clone = (b) => b.map((row) => row.map((t) => t));
const adjacent = (a, b) => Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) === 1;

function findRuns(board, valueOf) {
    const cells = new Set();
    let score = 0;
    // horizontal
    for (let r = 0; r < SIZE; r++) {
        let run = 1;
        for (let c = 1; c <= SIZE; c++) {
            const same = c < SIZE && board[r][c] && board[r][c - 1] && board[r][c].type === board[r][c - 1].type;
            if (same) { run++; } else {
                if (run >= 3) {
                    const t = board[r][c - 1].type;
                    score += Math.round(run * valueOf(t) * bonus(run));
                    for (let x = c - run; x < c; x++) cells.add(`${r},${x}`);
                }
                run = 1;
            }
        }
    }
    // vertical
    for (let c = 0; c < SIZE; c++) {
        let run = 1;
        for (let r = 1; r <= SIZE; r++) {
            const same = r < SIZE && board[r][c] && board[r - 1][c] && board[r][c].type === board[r - 1][c].type;
            if (same) { run++; } else {
                if (run >= 3) {
                    const t = board[r - 1][c].type;
                    score += Math.round(run * valueOf(t) * bonus(run));
                    for (let x = r - run; x < r; x++) cells.add(`${x},${c}`);
                }
                run = 1;
            }
        }
    }
    return { cells, score };
}

function collapse(board, cells) {
    const b = clone(board);
    cells.forEach((k) => { const [r, c] = k.split(',').map(Number); b[r][c] = null; });
    for (let c = 0; c < SIZE; c++) {
        const stack = [];
        for (let r = SIZE - 1; r >= 0; r--) if (b[r][c]) stack.push(b[r][c]);
        for (let r = SIZE - 1; r >= 0; r--) b[r][c] = stack[SIZE - 1 - r] || randTile();
    }
    return b;
}

export default function PulseCrush({ game }) {
    const [data, setData] = useState(null);            // levels payload
    const [phase, setPhase] = useState('loading');     // loading|levels|playing|question|cleared|failed
    const [cfg, setCfg] = useState(null);              // active level config
    const [board, setBoard] = useState([]);
    const [selected, setSelected] = useState(null);
    const [matched, setMatched] = useState(new Set());
    const [score, setScore] = useState(0);
    const [moves, setMoves] = useState(0);
    const [question, setQuestion] = useState(null);
    const [picked, setPicked] = useState(null);
    const [answerRes, setAnswerRes] = useState(null);
    const [finalize, setFinalize] = useState(null);
    const [busy, setBusy] = useState(false);
    const [flash, setFlash] = useState(null);          // floating +pts/+moves toast

    const resolving = useRef(false);
    const scoreRef = useRef(0);
    const movesRef = useRef(0);
    const maxScoreRef = useRef(0);
    const slabRef = useRef(0);
    const qplRef = useRef(0);
    const movesPerCorrectRef = useRef(0);
    const maxMovesRef = useRef(0);
    const unlockedRef = useRef(0);       // # of skill questions unlocked so far
    const answeredRef = useRef(0);       // # of skill questions answered so far
    const questionActiveRef = useRef(false);
    const endedRef = useRef(false);      // level cleared or failed
    const attemptRef = useRef(null);
    const tileValRef = useRef({});
    const tileImgRef = useRef({});

    const valueOf = useCallback((t) => (tileValRef.current[t] ?? 0), []);

    const loadLevels = useCallback(() => {
        api.get('/games/pulse-crush/levels')
            .then((r) => { setData(r.data); setPhase('levels'); })
            .catch(() => { setData({ levels: [], tiles: [] }); setPhase('levels'); });
    }, []);
    useEffect(() => { loadLevels(); }, [loadLevels]);

    const applyTiles = (tiles) => {
        const vals = {}; const imgs = {};
        (tiles || []).forEach((t) => { vals[t.type] = t.value; if (t.image_url) imgs[t.type] = t.image_url; });
        TYPES.forEach((t) => { if (vals[t] === undefined) vals[t] = TILE_META[t] ? 1 : 0; });
        tileValRef.current = vals; tileImgRef.current = imgs;
    };

    const flashToast = (msg) => { setFlash(msg); setTimeout(() => setFlash(null), 1400); };

    // ── level lifecycle ───────────────────────────────────────────────────────
    const doClear = useCallback(async () => {
        if (endedRef.current) return; endedRef.current = true;
        try {
            const r = await api.post('/games/pulse-crush/finish', { attempt_id: attemptRef.current, outcome: 'cleared' });
            setFinalize(r.data.finalize);
        } catch { /* keep the result screen even if the sync hiccups */ }
        setPhase('cleared');
    }, []);

    const doFail = useCallback(async () => {
        if (endedRef.current) return; endedRef.current = true;
        try {
            const r = await api.post('/games/pulse-crush/finish', { attempt_id: attemptRef.current, outcome: 'failed' });
            setFinalize(r.data.finalize);
        } catch { /* keep the result screen even if the sync hiccups */ }
        setPhase('failed');
    }, []);

    const doUnlock = useCallback(async () => {
        questionActiveRef.current = true;
        unlockedRef.current += 1;
        setPhase('question');
        try {
            const r = await api.post('/games/pulse-crush/unlock', { attempt_id: attemptRef.current });
            setQuestion(r.data.question); setPicked(null); setAnswerRes(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Unlock failed');
            questionActiveRef.current = false; unlockedRef.current -= 1; setPhase('playing');
        }
    }, []);

    // Pop a skill question once the score bar crosses the next slab threshold.
    const maybeUnlock = useCallback(() => {
        if (questionActiveRef.current || endedRef.current) return false;
        if (unlockedRef.current >= qplRef.current) return false;
        const threshold = Math.min((unlockedRef.current + 1) * slabRef.current, maxScoreRef.current);
        if (scoreRef.current >= threshold) { doUnlock(); return true; }
        return false;
    }, [doUnlock]);

    const addScore = useCallback((delta) => {
        scoreRef.current = Math.max(0, Math.min(maxScoreRef.current, scoreRef.current + delta));
        setScore(scoreRef.current);
        maybeUnlock();
    }, [maybeUnlock]);

    const startLevel = async (level) => {
        setBusy(true);
        try {
            const r = await api.post('/games/pulse-crush/start', { level });
            const d = r.data;
            applyTiles(d.tiles);
            attemptRef.current = d.attempt_id;
            scoreRef.current = 0; movesRef.current = d.moves;
            maxScoreRef.current = d.max_score; slabRef.current = d.slab; qplRef.current = d.questions_per_level;
            movesPerCorrectRef.current = d.moves_per_correct; maxMovesRef.current = d.max_moves;
            unlockedRef.current = 0; answeredRef.current = 0;
            questionActiveRef.current = false; endedRef.current = false;
            setCfg(d);
            setScore(0); setMoves(d.moves);
            setBoard(newBoard()); setSelected(null); setMatched(new Set());
            setQuestion(null); setPicked(null); setAnswerRes(null); setFinalize(null); setFlash(null);
            setPhase('playing');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not start level');
        } finally { setBusy(false); }
    };

    const resolve = async (start) => {
        let b = start; let gained = 0;
        while (true) {
            const { cells, score: sc } = findRuns(b, valueOf);
            if (cells.size === 0) break;
            gained += sc;
            setMatched(cells); await sleep(170);
            b = collapse(b, cells); setBoard(b); setMatched(new Set()); await sleep(140);
        }
        if (gained !== 0) addScore(gained);
        return b;
    };

    // Swap two adjacent tiles (shared by tap AND swipe) — reverts if no match forms.
    // A successful swap costs ONE move; an invalid swap costs nothing.
    const swapTiles = async (sr, sc, r, c) => {
        if (phase !== 'playing' || resolving.current || endedRef.current) return;
        if (!adjacent([sr, sc], [r, c])) { setSelected([r, c]); return; }
        setSelected(null); resolving.current = true;
        let b = clone(board);
        const tmp = b[sr][sc]; b[sr][sc] = b[r][c]; b[r][c] = tmp;
        setBoard(b); await sleep(140);
        const { cells } = findRuns(b, valueOf);
        if (cells.size === 0) {
            const b2 = clone(b); const t2 = b2[sr][sc]; b2[sr][sc] = b2[r][c]; b2[r][c] = t2;
            setBoard(b2); resolving.current = false; return;
        }
        movesRef.current = Math.max(0, movesRef.current - 1);
        setMoves(movesRef.current);
        await resolve(b);
        resolving.current = false;
        // out of moves with the bar not yet full (and no pending question) → level fails
        if (!questionActiveRef.current && !endedRef.current
            && movesRef.current <= 0 && scoreRef.current < maxScoreRef.current) {
            doFail();
        }
    };

    // Tap fallback: tap a tile, then tap an adjacent tile to swap.
    const onTile = (r, c) => {
        if (phase !== 'playing' || resolving.current || endedRef.current) return;
        if (!selected) { setSelected([r, c]); return; }
        const [sr, sc] = selected;
        if (sr === r && sc === c) { setSelected(null); return; }
        swapTiles(sr, sc, r, c);
    };

    const answer = async (i) => {
        if (busy || answerRes) return;
        setPicked(i); setBusy(true);
        try {
            const r = await api.post('/games/pulse-crush/answer', { attempt_id: attemptRef.current, selected_index: i });
            setAnswerRes(r.data);
            if (r.data.correct) {
                const add = r.data.bonus_moves || 0;
                movesRef.current = Math.min(maxMovesRef.current, movesRef.current + add);
                setMoves(movesRef.current);
                flashToast(`+${r.data.points_earned} pts${add ? ` · +${add} moves` : ''}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Answer failed'); setPicked(null);
        } finally { setBusy(false); }
    };

    const afterAnswer = () => {
        if (!answerRes) return;
        answeredRef.current += 1;
        questionActiveRef.current = false;
        setQuestion(null); setAnswerRes(null); setPicked(null);
        if (answeredRef.current >= qplRef.current) { doClear(); return; }
        setPhase('playing');
        // a big cascade may already have crossed the next slab; otherwise check the fail state
        const popped = maybeUnlock();
        if (!popped && !questionActiveRef.current && movesRef.current <= 0 && scoreRef.current < maxScoreRef.current) {
            doFail();
        }
    };

    if (phase === 'loading') {
        return <div className="min-h-[70vh] grid place-items-center" style={{ background: CR.body }}><Loader2 className="h-7 w-7 animate-spin" style={{ color: CR.gold }} /></div>;
    }

    return (
        <div data-testid="pulse-crush" className="min-h-[80vh]" style={{ background: CR.body }}>
            {phase === 'levels' && <LevelSelect data={data} onPick={startLevel} busy={busy} />}
            {(phase === 'playing' || phase === 'question') && cfg && (
                <PlayScreen
                    cfg={cfg} board={board} selected={selected} matched={matched} score={score}
                    maxScore={cfg.max_score} moves={moves} startMoves={cfg.start_moves}
                    onTile={onTile} onSwap={swapTiles} tileImg={tileImgRef.current} flash={flash}
                    slabIdx={unlockedRef.current} slabTotal={cfg.questions_per_level}
                    question={phase === 'question' ? question : null}
                    picked={picked} answerRes={answerRes} busy={busy} onAnswer={answer} onContinue={afterAnswer}
                    onQuit={() => { setPhase('levels'); loadLevels(); }}
                />
            )}
            {phase === 'cleared' && <ResultScreen ok cfg={cfg} finalize={finalize} onReplay={() => startLevel(cfg.level)} onLevels={() => { setPhase('levels'); loadLevels(); }} />}
            {phase === 'failed' && <ResultScreen ok={false} cfg={cfg} finalize={finalize} onReplay={() => startLevel(cfg.level)} onLevels={() => { setPhase('levels'); loadLevels(); }} />}
        </div>
    );
}

function LevelSelect({ data, onPick, busy }) {
    const levels = data?.levels || [];
    const tiles = data?.tiles || [];
    return (
        <div className="max-w-xl mx-auto px-4 py-6" data-testid="crush-levels">
            <div className="flex items-center gap-3">
                <span className="grid place-items-center w-11 h-11 rounded-2xl" style={{ background: CR.gold }}>
                    <Sparkles className="h-6 w-6" style={{ color: CR.ink }} />
                </span>
                <div>
                    <h1 className="font-display font-black text-2xl text-white leading-none">Pulse Crush</h1>
                    <p className="text-xs text-white/60 mt-1">Match items · fill the bar · answer to earn moves</p>
                </div>
                <div className="ml-auto text-right">
                    <div className="text-[10px] uppercase tracking-wide text-white/50">My points</div>
                    <div className="font-display font-black text-xl" style={{ color: CR.gold }} data-testid="crush-my-total">{data?.my_total || 0}</div>
                </div>
            </div>

            {/* tiles legend */}
            <div className="mt-5 flex flex-wrap gap-2">
                {tiles.map((t) => {
                    const m = TILE_META[t.type] || {}; const Icon = m.Icon || Package;
                    const img = (t.image_url) || DEFAULT_TILE_IMG[t.type];
                    return (
                        <div key={t.type} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-bold text-white/90" style={{ background: 'rgba(255,255,255,0.06)' }}>
                            <span className="grid place-items-center w-6 h-6 rounded-md overflow-hidden" style={{ background: img ? 'rgba(255,255,255,0.06)' : m.color }}>
                                {img
                                    ? <img src={tileSrc(img)} alt="" className="w-full h-full object-contain p-0.5" />
                                    : <Icon className="h-3 w-3" style={{ color: '#1a1400' }} />}
                            </span>
                            {m.label} <span style={{ color: t.value < 0 ? CR.danger : CR.gold }}>{t.value > 0 ? `+${t.value}` : t.value}</span>
                        </div>
                    );
                })}
            </div>

            <div className="mt-5 grid gap-3">
                {levels.map((lv) => {
                    const locked = !lv.released;
                    const noQ = lv.released && lv.questions_in_bank === 0;
                    const disabled = locked || noQ || busy;
                    return (
                        <button key={lv.level} disabled={disabled} onClick={() => onPick(lv.level)}
                            data-testid={`crush-level-${lv.level}`}
                            className="flex items-center gap-4 rounded-2xl px-4 py-3.5 text-left transition disabled:opacity-60"
                            style={{ background: lv.my_best > 0 ? 'rgba(0,219,121,0.14)' : 'rgba(255,255,255,0.05)', boxShadow: `inset 0 0 0 1.5px ${lv.my_best > 0 ? CR.gold : 'rgba(255,255,255,0.08)'}` }}>
                            <div className="grid place-items-center w-12 h-12 rounded-xl font-display font-black text-lg" style={{ background: CR.gold, color: CR.ink }}>
                                {locked ? <Lock className="h-5 w-5" /> : lv.level}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-white">Level {lv.level} <span className="text-white/50 text-xs font-semibold">· up to +{lv.max_reward} pts</span></div>
                                <div className="text-[12px] text-white/60 mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
                                    <span className="inline-flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Bar {lv.max_score}</span>
                                    <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> {lv.questions_per_level} Qs · +{lv.per_correct}/correct</span>
                                    {lv.my_best > 0 && <span className="inline-flex items-center gap-1" style={{ color: CR.gold }}><Trophy className="h-3 w-3" /> Best {lv.my_best}</span>}
                                </div>
                            </div>
                            <div className="shrink-0">
                                {locked ? <span className="text-[11px] font-bold text-white/50">Coming soon</span>
                                    : noQ ? <span className="text-[11px] font-bold text-amber-300">No questions</span>
                                        : <span className="inline-flex items-center gap-1 text-sm font-black" style={{ color: CR.gold }}><Play className="h-4 w-4" /> Play</span>}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function PlayScreen({ cfg, board, selected, matched, score, maxScore, moves, startMoves, onTile, onSwap, tileImg, flash, slabIdx, slabTotal, question, picked, answerRes, busy, onAnswer, onContinue, onQuit }) {
    const pct = Math.min(100, Math.round((score / maxScore) * 100));
    const lowMoves = moves <= 5;
    // Swipe/drag to swap (doc Point 24): press a tile and flick toward an adjacent one.
    const dragRef = useRef(null);
    const SWIPE_TH = 16;
    const onPointerDown = (e, r, c) => { dragRef.current = { r, c, x: e.clientX, y: e.clientY }; };
    const onPointerUp = (e) => {
        const d = dragRef.current; dragRef.current = null;
        if (!d) return;
        const dx = e.clientX - d.x, dy = e.clientY - d.y;
        if (Math.abs(dx) < SWIPE_TH && Math.abs(dy) < SWIPE_TH) { onTile(d.r, d.c); return; } // tap
        let tr = d.r, tc = d.c;
        if (Math.abs(dx) > Math.abs(dy)) tc += dx > 0 ? 1 : -1;
        else tr += dy > 0 ? 1 : -1;
        if (tr < 0 || tc < 0 || tr >= SIZE || tc >= SIZE) return;
        onSwap(d.r, d.c, tr, tc);
    };
    return (
        <div className="max-w-md mx-auto px-3 py-4" data-testid="crush-play">
            {/* HUD */}
            <div className="flex items-center justify-between mb-2">
                <button onClick={onQuit} className="inline-flex items-center gap-1 text-xs font-semibold text-white/60 hover:text-white" data-testid="crush-quit-btn">
                    <ChevronLeft className="h-4 w-4" /> Levels
                </button>
                <div className="font-display font-black text-white text-sm">Level {cfg.level}</div>
                <div className="inline-flex items-center gap-1 text-[11px] font-bold text-white/70" data-testid="crush-slab-count">
                    <Sparkles className="h-3.5 w-3.5" style={{ color: CR.gold }} /> {Math.min(slabIdx, slabTotal)}/{slabTotal} Qs
                </div>
            </div>

            {/* score bar + moves counter */}
            <div className="flex items-center gap-3 mb-2">
                <div className="flex-1">
                    <div className="flex items-center justify-between text-[11px] font-bold mb-1">
                        <span className="text-white/70">Score <span style={{ color: CR.gold }} data-testid="crush-score">{score}</span> / {maxScore}</span>
                        <span className="text-white/40">{pct}%</span>
                    </div>
                    <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                        <div className="h-full rounded-full transition-all duration-300" style={{ width: `${pct}%`, background: CR.gold }} />
                    </div>
                </div>
                <div className="grid place-items-center w-14 h-14 rounded-2xl font-display font-black" data-testid="crush-moves"
                    style={{ background: lowMoves ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.07)', color: lowMoves ? CR.danger : '#fff', boxShadow: `inset 0 0 0 1.5px ${lowMoves ? CR.danger : 'rgba(255,255,255,0.15)'}` }}>
                    <Move className="h-3.5 w-3.5 mb-0.5" />
                    <span className="text-sm leading-none">{moves}</span>
                </div>
            </div>

            {/* board */}
            <div className="relative rounded-2xl p-1.5" style={{ background: CR.grid, boxShadow: `inset 0 0 0 2px ${CR.gold}55` }}>
                {/* floating +pts/+moves toast */}
                <AnimatePresence>
                    {flash && (
                        <motion.div key={flash} initial={{ opacity: 0, y: 8, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8 }}
                            className="absolute left-1/2 -translate-x-1/2 top-2 z-20 px-3 py-1.5 rounded-full font-display font-black text-sm"
                            style={{ background: CR.gold, color: CR.ink }} data-testid="crush-flash">{flash}</motion.div>
                    )}
                </AnimatePresence>
                <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${SIZE}, minmax(0, 1fr))` }}>
                    {board.map((row, r) => row.map((cell, c) => {
                        const m = TILE_META[cell.type] || {}; const Icon = m.Icon || Package;
                        const isSel = selected && selected[0] === r && selected[1] === c;
                        const isMatched = matched.has(`${r},${c}`);
                        const img = tileImg[cell.type] || DEFAULT_TILE_IMG[cell.type];
                        return (
                            <button key={cell.id}
                                onPointerDown={(e) => onPointerDown(e, r, c)}
                                onPointerUp={onPointerUp}
                                data-testid={`crush-tile-${r}-${c}`}
                                className="relative aspect-square rounded-lg grid place-items-center transition-transform touch-none select-none"
                                style={{
                                    background: img ? 'rgba(255,255,255,0.05)' : m.color,
                                    boxShadow: isSel
                                        ? `0 0 0 3px ${CR.gold}, 0 0 14px ${CR.gold}`
                                        : `inset 0 0 0 1.5px ${(m.color || '#fff')}44`,
                                    transform: isMatched ? 'scale(0.55)' : isSel ? 'scale(1.06)' : 'scale(1)',
                                    opacity: isMatched ? 0.25 : 1,
                                }}>
                                {img
                                    ? <img src={tileSrc(img)} alt="" draggable={false} className="w-full h-full object-contain p-1 pointer-events-none" />
                                    : <Icon className="h-1/2 w-1/2" style={{ color: '#1a1400' }} strokeWidth={2.4} />}
                            </button>
                        );
                    }))}
                </div>

                {/* question overlay */}
                <AnimatePresence>
                    {question && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="absolute inset-0 rounded-2xl p-3 flex flex-col" style={{ background: 'rgba(2,22,14,0.95)', backdropFilter: 'blur(3px)' }}
                            data-testid="crush-question">
                            <div className="text-center">
                                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wide" style={{ background: CR.gold, color: CR.ink }}>
                                    <Sparkles className="h-3 w-3" /> Skill Question — correct = points + moves!
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto mt-3">
                                {question.image_url && <img src={mediaUrl(question.image_url)} alt="" className="max-h-28 rounded-xl mx-auto mb-3" />}
                                <p className="font-display font-bold text-white text-[15px] leading-snug text-center px-1">{question.question}</p>
                                <div className="grid gap-2 mt-4">
                                    {question.options.map((o, i) => {
                                        const isCorrect = answerRes && i === answerRes.correct_index;
                                        const isPicked = picked === i;
                                        let bg = 'rgba(255,255,255,0.07)'; let ring = 'rgba(255,255,255,0.12)'; let col = '#fff';
                                        if (answerRes) {
                                            if (isCorrect) { bg = 'rgba(34,197,94,0.22)'; ring = CR.green; }
                                            else if (isPicked) { bg = 'rgba(239,68,68,0.22)'; ring = CR.danger; }
                                            else { col = 'rgba(255,255,255,0.5)'; }
                                        }
                                        return (
                                            <button key={i} disabled={!!answerRes || busy} onClick={() => onAnswer(i)}
                                                data-testid={`crush-option-${i}`}
                                                className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition"
                                                style={{ background: bg, color: col, boxShadow: `inset 0 0 0 1.5px ${ring}` }}>
                                                <span className="grid place-items-center w-6 h-6 rounded-md text-xs font-black shrink-0" style={{ background: 'rgba(0,0,0,0.25)' }}>{String.fromCharCode(65 + i)}</span>
                                                <span className="flex-1">{o}</span>
                                                {answerRes && isCorrect && <CheckCircle2 className="h-4 w-4" style={{ color: CR.green }} />}
                                                {answerRes && isPicked && !isCorrect && <XCircle className="h-4 w-4" style={{ color: CR.danger }} />}
                                            </button>
                                        );
                                    })}
                                </div>
                                {answerRes && answerRes.explanation && (
                                    <p className="text-[12px] text-white/70 mt-3 leading-relaxed">{answerRes.explanation}</p>
                                )}
                            </div>
                            {answerRes && (
                                <button onClick={onContinue} data-testid="crush-continue-btn"
                                    className="mt-3 w-full rounded-xl py-2.5 font-display font-black text-sm" style={{ background: CR.gold, color: CR.ink }}>
                                    {answerRes.correct ? 'Correct! Keep matching' : 'Keep matching'}
                                </button>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            <p className="text-center text-[11px] text-white/40 mt-2">Swipe an item toward a neighbour to swap and match 3+. Each successful move counts!</p>
        </div>
    );
}

function ResultScreen({ ok, cfg, finalize, onReplay, onLevels }) {
    return (
        <div className="max-w-sm mx-auto px-4 py-10 text-center" data-testid={ok ? 'crush-cleared' : 'crush-failed'}>
            <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 220, damping: 16 }}
                className="grid place-items-center w-24 h-24 mx-auto rounded-full" style={{ background: ok ? 'rgba(34,197,94,0.18)' : 'rgba(239,68,68,0.16)', boxShadow: `inset 0 0 0 2px ${ok ? CR.green : CR.danger}` }}>
                {ok ? <Trophy className="h-11 w-11" style={{ color: CR.gold }} /> : <XCircle className="h-11 w-11" style={{ color: CR.danger }} />}
            </motion.div>
            <h2 className="font-display font-black text-2xl text-white mt-5">{ok ? `Level ${cfg.level} cleared!` : 'Out of moves'}</h2>
            <p className="text-sm text-white/60 mt-1">{ok ? 'Bar filled — great matching!' : 'You ran out of moves — give it another go.'}</p>
            {finalize && (
                <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-2xl" style={{ background: 'rgba(0,219,121,0.15)', boxShadow: `inset 0 0 0 1.5px ${CR.gold}66` }}>
                    <Sparkles className="h-4 w-4" style={{ color: CR.gold }} />
                    <span className="font-bold text-white text-sm">+{finalize.points_added} pts</span>
                    <span className="text-white/50 text-xs">· Total {finalize.total_best}</span>
                </div>
            )}
            <div className="mt-7 grid gap-2.5">
                <button onClick={onReplay} data-testid="crush-replay-btn" className="w-full rounded-2xl py-3 font-display font-black inline-flex items-center justify-center gap-2" style={{ background: CR.gold, color: CR.ink }}>
                    <RotateCcw className="h-4 w-4" /> Play again
                </button>
                <button onClick={onLevels} data-testid="crush-levels-btn" className="w-full rounded-2xl py-3 font-bold text-white/80" style={{ background: 'rgba(255,255,255,0.07)' }}>
                    Choose a level
                </button>
            </div>
        </div>
    );
}
